import React, { useEffect } from 'react';
import { AxiosError } from 'axios';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import inLocale from 'date-fns/locale/en-IN';
import {
    Query, QueryCache, QueryClient, QueryClientProvider,
} from 'react-query';
import { SnackbarProvider } from 'notistack';

import { useLocation } from 'react-router-dom';
import useStore from './store/store';
import {
    AlertBox,
    ClientDateTimeErrorComponent,
    SessionTimeoutDialog,
    Spinner,
    useValidateClientDateTime,
} from './components';
import {
    didApiCallFailDueToUnauthorizedUser,
    didApiCallFailDueToVersionMismatch,
    getErrorMessage,
} from './utils/apiUtils';
import { Store } from './store/types';
import { ErrorType } from './types/commonTypes';
import AppVersionMismatchDialog from './components/AppVersionMismatchDialog/AppVersionMismatchDialog';
import useValidateSession from './hooks/useValidateSession';
import { useCommonMessageAndSpinnerHandlers } from './hooks/useCommonMessageAndSpinnerHandlers';
import NavigationContainer from './containers/NavigationBarContainer/NavigationContainer';
import { getAppVersion } from './utils/localStorage';
import { isObjectValidAndNotEmpty } from './utils/nullChecks';
import useFetchAppConfiguration from './hooks/useFetchAppConfiguration';

type Props = {
    children: React.ReactNode,
};

const AppContainerWithoutProviders = (props: Props) => {
    const { children } = props;

    // Authenticating user
    const location = useLocation();
    useValidateSession(location.pathname);

    // Client time validation
    const hasClientDateTimeError = useValidateClientDateTime();

    // User Privilege validation

    // >>>> APP VERSION
    const appVersion = getAppVersion();

    // >>>> APP CONFIGURATION
    const [
        { data: appConfiguration },
        { data: qzConfigurationResponse },
    ] = useFetchAppConfiguration(appVersion);
    const qzConfiguration = qzConfigurationResponse?.data;
    const setAppConfiguration = useStore((state: Store) => state.setAppConfiguration);

    useEffect(() => {
        if (isObjectValidAndNotEmpty(appConfiguration)) {
            setAppConfiguration({
                ...appConfiguration,
                ...qzConfiguration,
            });
        }
    }, [appConfiguration, qzConfiguration, setAppConfiguration]);

    let childNode = children;
    if (hasClientDateTimeError) {
        childNode = <ClientDateTimeErrorComponent />;
    }

    // Checking privilege for a tab

    return (
        <>
            <SessionTimeoutDialog />
            <AppVersionMismatchDialog />
            <AlertBox />
            <Spinner />
            <NavigationContainer>
                {(childNode)}
            </NavigationContainer>

        </>
    );
};

const AppContainerWithoutSnackbarProvider = (props: Props) => {
    const { children } = props;
    const { errorMessage } = useCommonMessageAndSpinnerHandlers();
    const setSessionTimeout = useStore((state: Store) => state.setSessionTimeout);
    const setAppVersionMismatch = useStore((state: Store) => state.setAppVersionMismatch);

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                retry: false,
            },
        },
        queryCache: new QueryCache({
            onError: async (e: unknown, query: Query) => {
                const error = e as AxiosError<ErrorType>;
                if (didApiCallFailDueToUnauthorizedUser(error)) {
                    setSessionTimeout({
                        timeout: true,
                        successCallback: () => { queryClient.refetchQueries(query.queryKey); },
                    });
                } else if (didApiCallFailDueToVersionMismatch(error)) {
                    setAppVersionMismatch(true);
                } else {
                    errorMessage(getErrorMessage(error));
                }
            },
        }),
    });

    return (
        <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={inLocale}>
                <AppContainerWithoutProviders>
                    {children}
                </AppContainerWithoutProviders>
            </LocalizationProvider>
        </QueryClientProvider>
    );
};

const AppContainer = (props: Props) => {
    const { children } = props;

    return (
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} autoHideDuration={3000}>
            <AppContainerWithoutSnackbarProvider>
                {children}
            </AppContainerWithoutSnackbarProvider>
        </SnackbarProvider>
    );
};

export default AppContainer;
